import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useCookieConsent, CookieConsent } from '@/hooks/useCookieConsent';
import { Shield, Wrench, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';

interface CookieConsentModalProps {
  onClose: () => void;
}

export const CookieConsentModal = ({ onClose }: CookieConsentModalProps) => {
  const { 
    essential, 
    functional, 
    analytics, 
    marketing, 
    updateConsent, 
    acceptAll, 
    acceptEssential 
  } = useCookieConsent();

  const [tempConsent, setTempConsent] = useState<CookieConsent>({
    essential,
    functional,
    analytics: false,
    marketing: false,
  });

  const handleSavePreferences = () => {
    updateConsent(tempConsent);
    onClose();
  };

  const handleAcceptAll = () => {
    acceptAll();
    onClose();
  };

  const handleAcceptEssential = () => {
    acceptEssential();
    onClose();
  };

  const cookieCategories = [
    {
      id: 'essential' as const,
      title: 'Essenzielle Cookies',
      description: 'Diese Cookies sind für das ordnungsgemäße Funktionieren der Website erforderlich und können nicht deaktiviert werden.',
      icon: Shield,
      required: true,
      examples: 'Sitzungs-ID, Sicherheits-Token, Anmeldestatus',
    },
    {
      id: 'functional' as const,
      title: 'Funktionale Cookies',
      description: 'Diese Cookies ermöglichen erweiterte Funktionen und Personalisierung, wie Spracheinstellungen und Benutzervorlieben.',
      icon: Wrench,
      required: false,
      examples: 'Spracheinstellungen, Theme-Präferenzen, Formular-Daten, Barrierefreiheit-Einstellungen',
    },
    {
      id: 'analytics' as const,
      title: 'Analyse-Cookies',
      description: 'Helfen uns zu verstehen, wie Besucher die Website nutzen. Alle Daten werden anonymisiert erhoben.',
      icon: BarChart3,
      required: false,
      examples: 'Google Analytics, Seitenaufrufe, Verweildauer, Besucherströme',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="text-sm text-muted-foreground">
        <p className="mb-2">
          Wir verwenden verschiedene Arten von Cookies, um Ihnen die bestmögliche Erfahrung 
          auf unserer Website zu bieten. Sie können auswählen, welche Kategorien Sie zulassen möchten.
        </p>
        <p>
          Weitere Informationen finden Sie in unserer{' '}
          <Link to="/datenschutz" className="text-primary hover:underline">
            Datenschutzerklärung
          </Link>
          .
        </p>
      </div>

      <div className="space-y-4 max-h-96 overflow-y-auto">
        {cookieCategories.map((category) => {
          const Icon = category.icon;
          const isEnabled = tempConsent[category.id];
          
          return (
            <Card key={category.id} className="border">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <Icon className="h-5 w-5 text-primary flex-shrink-0" />
                    <div className="flex-1">
                      <CardTitle className="text-base flex items-center gap-2">
                        {category.title}
                        {category.required && (
                          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                            Erforderlich
                          </span>
                        )}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        {category.description}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={isEnabled}
                    disabled={category.required}
                    onCheckedChange={(checked) => 
                      setTempConsent(prev => ({ ...prev, [category.id]: checked }))
                    }
                  />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-xs text-muted-foreground">
                  <strong>Beispiele:</strong> {category.examples}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Separator />

      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          variant="outline"
          onClick={handleAcceptEssential}
          className="flex-1"
        >
          Nur Essenzielle
        </Button>
        <Button
          variant="outline"
          onClick={handleSavePreferences}
          className="flex-1"
        >
          Auswahl speichern
        </Button>
        <Button
          onClick={handleAcceptAll}
          className="flex-1"
        >
          Alle akzeptieren
        </Button>
      </div>
    </div>
  );
};